import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { RoomType } from '../types';

const ROOM_TYPES: Record<string, RoomType> = {
  living_room: RoomType.LIVING_ROOM, bedroom: RoomType.BEDROOM,
  kitchen: RoomType.KITCHEN, bathroom: RoomType.BATHROOM, office: RoomType.OFFICE,
};
export function useRoomDetection(file: File | null, onDetected: (room: RoomType) => void, assignedRoom?: RoomType | null) {
  const [status, setStatus] = useState<'idle' | 'detecting' | 'detected' | 'manual' | 'failed'>('idle');
  const callback = useRef(onDetected);
  callback.current = onDetected;
  const generation = useRef(0);
  const overridden = useRef(false);
  useEffect(() => {
    const current = ++generation.current;
    overridden.current = false;
    if (!file) { setStatus('idle'); return; }
    if (assignedRoom) { overridden.current = true; setStatus('manual'); return; }
    setStatus('detecting');
    let timer: ReturnType<typeof setTimeout>;
    const reader = new FileReader();
    const detect = async () => {
      try {
        const image = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(new Error('Foto lezen mislukt'));
          reader.onabort = () => reject(new Error('Foto lezen afgebroken'));
          reader.readAsDataURL(file);
        });
        const data = await api.post<{ room: string; manualSelectionRequired?: boolean }>('/api/ai/classify-photo', { image, detectionOnly: true });
        if (generation.current !== current || overridden.current) return;
        const room = ROOM_TYPES[data.room];
        if (!room || data.manualSelectionRequired) { setStatus('failed'); return; }
        callback.current(room);
        setStatus('detected');
      } catch {
        if (generation.current === current && !overridden.current) setStatus('failed');
      } finally { clearTimeout(timer); }
    };
    // UI fallback also covers network hangs; late results may not overwrite a choice.
    timer = setTimeout(() => {
      if (generation.current === current && !overridden.current) {
        overridden.current = true;
        setStatus('failed');
      }
    }, 20000);
    void detect();
    return () => { ++generation.current; clearTimeout(timer); if (reader.readyState === 1) reader.abort(); };
  }, [file, assignedRoom]);
  return { status, override(room: RoomType) {
    overridden.current = true;
    callback.current(room);
    setStatus('manual');
  } };
}
